export type PageMetaInput = {
  title: string;
  description: string;
  path?: string;
  keywords?: string;
  robots?: string;
  ogType?: string;
  noindex?: boolean;
};

export const SITE_META = {
  name: 'Boticlick',
  defaultTitle: 'Boticlick | Farmacia y servicios de salud a domicilio',
  titleTemplate: (pageTitle: string) => `${pageTitle} | Boticlick`,
  defaultDescription:
    'Boticlick te conecta con farmacia, diagnostico express y salud en casa. Compra productos, arma tu carrito y recibe pedidos en tu domicilio.',
  defaultKeywords:
    'boticlick, farmacia, medicamentos, salud en casa, diagnostico express, delivery farmacia, productos de salud, Peru',
  locale: 'es_PE',
  themeColor: '#0073E6',
  ogImage: '/icons/farmacia.png',
  twitterCard: 'summary_large_image',
} as const;

const ROUTE_META: Record<string, Omit<PageMetaInput, 'path'>> = {
  '/': {
    title: 'Ingresa a tu cuenta',
    description:
      'Accede a Boticlick con tu telefono o registrate para comprar en farmacia y solicitar servicios de salud a domicilio.',
    keywords: 'boticlick login, registro boticlick, farmacia online',
  },
  '/verificar-codigo': {
    title: 'Verificar codigo',
    description: 'Confirma tu codigo de acceso para continuar en Boticlick.',
    robots: 'noindex, nofollow',
  },
  '/inicio': {
    title: 'Inicio',
    description:
      'Elige entre Salud en casa, Diagnostico Express o Farmacia y recibe atencion donde estes.',
    keywords: 'salud en casa, diagnostico express, farmacia boticlick',
  },
  '/productos': {
    title: 'Productos',
    description:
      'Explora categorias y productos destacados de farmacia, cuidado personal, belleza y bienestar en Boticlick.',
    keywords: 'productos farmacia, catalogo boticlick, medicamentos, cuidado personal',
  },
  '/pedido': {
    title: 'Finalizar pedido',
    description: 'Revisa tu carrito, confirma tu direccion de entrega y completa tu pedido en Boticlick.',
    robots: 'noindex, nofollow',
  },
  '/admin/login': {
    title: 'Admin login',
    description: 'Acceso al panel administrativo de Boticlick.',
    robots: 'noindex, nofollow',
    noindex: true,
  },
  '/admin': {
    title: 'Panel administrativo',
    description: 'Panel interno de gestion de Boticlick.',
    robots: 'noindex, nofollow',
    noindex: true,
  },
};

export function resolvePageMeta(pathname: string, override?: Partial<PageMetaInput>): PageMetaInput {
  const path = override?.path ?? pathname;
  const routeMeta = ROUTE_META[pathname] ?? matchDynamicRouteMeta(pathname);
  const title = override?.title ?? routeMeta?.title ?? SITE_META.defaultTitle;
  const description = override?.description ?? routeMeta?.description ?? SITE_META.defaultDescription;

  return {
    title,
    description,
    path,
    keywords: override?.keywords ?? routeMeta?.keywords ?? SITE_META.defaultKeywords,
    robots: override?.robots ?? routeMeta?.robots ?? (routeMeta?.noindex ? 'noindex, nofollow' : 'index, follow'),
    ogType: override?.ogType ?? 'website',
    noindex: override?.noindex ?? routeMeta?.noindex ?? false,
  };
}

function matchDynamicRouteMeta(pathname: string): Omit<PageMetaInput, 'path'> | null {
  if (pathname.startsWith('/categoria/')) {
    return {
      title: 'Categoria de productos',
      description: 'Compra productos por categoria en Boticlick y recibelos en tu domicilio.',
      keywords: 'categoria farmacia, productos boticlick',
    };
  }

  if (pathname.startsWith('/admin')) {
    return {
      title: 'Panel administrativo',
      description: 'Panel interno de gestion de Boticlick.',
      robots: 'noindex, nofollow',
      noindex: true,
    };
  }

  return null;
}

export function getSiteUrl(path: string): string {
  const baseUrl = process.env.REACT_APP_SITE_URL?.replace(/\/$/, '');

  if (baseUrl) {
    return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  }

  return path;
}

export function applyPageMeta(meta: PageMetaInput): void {
  const fullTitle = meta.title.includes('Boticlick') ? meta.title : SITE_META.titleTemplate(meta.title);
  document.title = fullTitle;

  upsertMetaTag('name', 'description', meta.description);
  upsertMetaTag('name', 'keywords', meta.keywords ?? SITE_META.defaultKeywords);
  upsertMetaTag('name', 'robots', meta.robots ?? 'index, follow');
  upsertMetaTag('name', 'author', SITE_META.name);
  upsertMetaTag('name', 'application-name', SITE_META.name);
  upsertMetaTag('name', 'theme-color', SITE_META.themeColor);

  upsertMetaTag('property', 'og:title', fullTitle);
  upsertMetaTag('property', 'og:description', meta.description);
  upsertMetaTag('property', 'og:type', meta.ogType ?? 'website');
  upsertMetaTag('property', 'og:site_name', SITE_META.name);
  upsertMetaTag('property', 'og:locale', SITE_META.locale);
  upsertMetaTag('property', 'og:url', getSiteUrl(meta.path ?? '/'));
  upsertMetaTag('property', 'og:image', getSiteUrl(SITE_META.ogImage));

  upsertMetaTag('name', 'twitter:card', SITE_META.twitterCard);
  upsertMetaTag('name', 'twitter:title', fullTitle);
  upsertMetaTag('name', 'twitter:description', meta.description);
  upsertMetaTag('name', 'twitter:image', getSiteUrl(SITE_META.ogImage));

  upsertLinkTag('canonical', getSiteUrl(meta.path ?? '/'));
}

function upsertMetaTag(attribute: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attribute}="${key}"]`;
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function upsertLinkTag(rel: string, href: string): void {
  let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}
